import { type FC } from 'react';
import { useFragment, graphql } from 'react-relay';
import { SharedRelatedItemsSection } from '../SharedRelatedItemsSection/SharedRelatedItemsSection.tsx';
import { recentSales } from '../../shared/sharedMessages.jsx';
import { filterFalsy } from 'dibs-ts-utils/exports/filterFalsy.ts';

import { type SharedRecentSales_viewer$key } from './__generated__/SharedRecentSales_viewer.graphql.ts';
import { type SharedRecentSales_itemSearch$key } from './__generated__/SharedRecentSales_itemSearch.graphql.ts';
import { type SharedRecentSales_buyPageSoldResults$key } from './__generated__/SharedRecentSales_buyPageSoldResults.graphql.ts';
import { type SharedRecentSales_user$key } from './__generated__/SharedRecentSales_user.graphql.ts';

const viewerFragment = graphql`
    fragment SharedRecentSales_viewer on Viewer {
        ...SharedRelatedItemsSection_viewer
    }
`;
const itemSearchFragment = graphql`
    fragment SharedRecentSales_itemSearch on ItemSearchQueryConnection {
        pageType
        ...SharedRelatedItemsSection_itemSearch
        soldItemResults: soldResults(first: 28) {
            edges {
                node {
                    item {
                        ...SharedRelatedItemsSection_item
                    }
                }
            }
        }
    }
`;
const buyPageSoldResultsFragment = graphql`
    fragment SharedRecentSales_buyPageSoldResults on BuyPageSoldResults {
        items(first: 28) {
            ...SharedRelatedItemsSection_item
        }
    }
`;
const userFragment = graphql`
    fragment SharedRecentSales_user on User {
        ...SharedRelatedItemsSection_user
    }
`;

type Props = {
    viewer: SharedRecentSales_viewer$key;
    itemSearch: SharedRecentSales_itemSearch$key;
    buyPageSoldResults?: SharedRecentSales_buyPageSoldResults$key | null;
    user: SharedRecentSales_user$key | null | undefined;
    isMobile: boolean;
    hideRecentSoldPrice?: boolean;
};

export const SharedRecentSales: FC<Props> = ({
    viewer: viewerRef,
    itemSearch: itemSearchRef,
    buyPageSoldResults: buyPageSoldResultsRef,
    user: userRef,
    isMobile,
    hideRecentSoldPrice,
}) => {
    const viewer = useFragment(viewerFragment, viewerRef);
    const itemSearch = useFragment(itemSearchFragment, itemSearchRef);
    const buyPageSoldResults = useFragment(buyPageSoldResultsFragment, buyPageSoldResultsRef);
    const user = useFragment(userFragment, userRef);

    const { soldItemResults, pageType } = itemSearch;
    const items = (
        buyPageSoldResults?.items ||
        soldItemResults?.edges?.map(edge => edge?.node?.item) ||
        []
    ).filter(filterFalsy);

    return (
        <SharedRelatedItemsSection
            viewer={viewer}
            itemSearch={itemSearch}
            items={items}
            user={user}
            initialRowCount={isMobile ? 2 : 1}
            header={recentSales}
            hideRecentSoldPrice={hideRecentSoldPrice}
            list={`${pageType} related sold items`}
        />
    );
};
